/**
 * DepartmentModal — Create a new department with optional member assignment.
 *
 * Features:
 *  - Department name input
 *  - Searchable user list with toggle assignment
 *  - Matches app glassmorphism design
 *
 * NOTE: Editing existing departments uses DepartmentDetailPanel (right drawer).
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import Building2 from 'lucide-react/dist/esm/icons/building-2';
import UserCheck from 'lucide-react/dist/esm/icons/user-check';
import UserMinus from 'lucide-react/dist/esm/icons/user-minus';
import type { Department, OrgUser } from '../../../services/api/org';
import { Button, Modal, TextField, SearchInput, Text } from '../../../components/ui';

interface DepartmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Should always be null (create mode). Kept for interface compat. */
  department: Department | null;
  users: OrgUser[];
  onSave: (name: string, memberIds: string[]) => Promise<void>;
}

const DepartmentModal: React.FC<DepartmentModalProps> = ({
  isOpen,
  onClose,
  department,
  users,
  onSave,
}) => {
  const nameRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setName('');
      setSelectedIds(new Set());
      setSearch('');
      setError(null);
      setSaving(false);
      setTimeout(() => nameRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Filter users by search
  const filteredUsers = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        (u.display_name || '').toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q)
    );
  }, [users, search]);

  // Sort: selected first, then alphabetical
  const sortedUsers = useMemo(() => {
    return [...filteredUsers].sort((a, b) => {
      const aSelected = selectedIds.has(a.id) ? 0 : 1;
      const bSelected = selectedIds.has(b.id) ? 0 : 1;
      if (aSelected !== bSelected) return aSelected - bSelected;
      return (a.display_name || a.email).localeCompare(b.display_name || b.email);
    });
  }, [filteredUsers, selectedIds]);

  const toggleUser = (userId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Department name is required');
      nameRef.current?.focus();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(trimmed, Array.from(selectedIds));
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to create department');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <Modal.Header
        title="New Department"
        icon={<Building2 size={16} />}
        onClose={onClose}
      />

      <Modal.Body>
        {/* Name Field */}
        <div className="mb-5">
          <TextField
            ref={nameRef}
            label="Department Name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (error) setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave();
            }}
            placeholder="e.g. Engineering, Finance, Operations..."
            error={error || undefined}
            size="lg"
          />
        </div>

        {/* Members Section */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <Text variant="label">Members</Text>
            <span className="text-2xs text-content-muted">
              {selectedIds.size} selected
            </span>
          </div>

          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search people..."
            onClear={() => setSearch('')}
            className="mb-3"
          />

          {/* User List */}
          <div className="space-y-1 max-h-[240px] overflow-y-auto pr-1 -mr-1">
            {sortedUsers.map((user) => {
              const isSelected = selectedIds.has(user.id);
              return (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => toggleUser(user.id)}
                  className={`
                    w-full flex items-center gap-3 p-2.5 rounded-xl text-left transition-all cursor-pointer border outline-none
                    ${isSelected
                      ? 'bg-brand/10 border-brand/20 hover:bg-brand/15'
                      : 'bg-transparent border-transparent hover:bg-surface-primary hover:border-subtle'
                    }
                  `}
                >
                  {/* Avatar */}
                  <div className={`
                    w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 transition-colors
                    ${isSelected
                      ? 'bg-brand/30 text-primary'
                      : 'bg-surface-secondary text-content-muted'
                    }
                  `}>
                    {(user.display_name || user.email)[0]?.toUpperCase()}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-content truncate">
                      {user.display_name || user.email.split('@')[0]}
                    </div>
                    <div className="text-[10px] text-content-muted truncate">
                      {user.email}
                    </div>
                  </div>

                  {/* Toggle icon */}
                  {isSelected ? (
                    <UserCheck size={14} className="text-primary shrink-0" />
                  ) : (
                    <UserMinus size={14} className="text-content-faint shrink-0" />
                  )}
                </button>
              );
            })}

            {sortedUsers.length === 0 && (
              <div className="text-center py-6">
                <p className="text-xs text-content-muted">
                  {search ? 'No matching people' : 'No people in the organization yet'}
                </p>
              </div>
            )}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="ghost" size="lg" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="lg"
          loading={saving}
          disabled={!name.trim()}
          onClick={handleSave}
        >
          Create Department
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default React.memo(DepartmentModal);
